// One designed tooltip for icon buttons and disabled reasons, in place of
// the native `title` (late, unstyled, outside the motion system). Any element
// with `data-hint="Label"` gets it on pointer hover or keyboard focus; an
// optional `data-hint-keys="["` (space-separated) adds key chips. Level 3
// (.sg-tooltip); it drops in from its trigger on --sg-fast and leaves on
// --sg-exit. Mounted once per shell; touch input never shows it.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MS, usePresence } from "./motion";

const SHOW_DELAY_MS = 380;
// Moving from one hinted control to the next within this window swaps the
// tip at once instead of waiting out the delay again.
const SWAP_WINDOW_MS = 320;
const GAP = 8;
const EDGE = 8;

const HintLayer = () => {
  const [tip, setTip] = useState(null);
  const layerRef = useRef(null);
  const presence = usePresence(Boolean(tip?.visible), { exitMs: MS.exit });

  useEffect(() => {
    let timer = 0;
    let current = null;
    let hiddenAt = 0;
    let shown = false;

    const place = (element) => {
      const label = element.getAttribute("data-hint");
      if (!label || !element.isConnected) return;
      const rect = element.getBoundingClientRect();
      const below = rect.top < 52;
      shown = true;
      setTip({
        label,
        keys: (element.getAttribute("data-hint-keys") || "")
          .split(/\s+/)
          .filter(Boolean),
        x: rect.left + rect.width / 2,
        y: below ? rect.bottom + GAP : rect.top - GAP,
        place: below ? "bottom" : "top",
        visible: true,
      });
    };

    const show = (element, delay) => {
      window.clearTimeout(timer);
      current = element;
      const swap = shown || performance.now() - hiddenAt < SWAP_WINDOW_MS;
      if (!delay || swap) place(element);
      else timer = window.setTimeout(() => place(element), delay);
    };

    const hide = () => {
      window.clearTimeout(timer);
      if (shown) hiddenAt = performance.now();
      shown = false;
      current = null;
      setTip((value) =>
        value?.visible ? { ...value, visible: false } : value,
      );
    };

    const onOver = (event) => {
      if (event.pointerType === "touch") return;
      const element =
        event.target instanceof Element
          ? event.target.closest("[data-hint]")
          : null;
      if (element === current) return;
      if (element) show(element, SHOW_DELAY_MS);
      else if (current) hide();
    };
    const onFocusIn = (event) => {
      const element =
        event.target instanceof Element
          ? event.target.closest("[data-hint]")
          : null;
      if (element && event.target.matches(":focus-visible")) show(element, 0);
      else if (current) hide();
    };
    const onKey = (event) => {
      if (event.key === "Escape" && current) hide();
    };

    document.addEventListener("pointerover", onOver, true);
    document.addEventListener("pointerdown", hide, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", hide, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    window.addEventListener("blur", hide);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerover", onOver, true);
      document.removeEventListener("pointerdown", hide, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", hide, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      window.removeEventListener("blur", hide);
    };
  }, []);

  // Clamp to the viewport once the tip's own width is known; written to the
  // layer's transform so placement never costs a second render.
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer || !tip) return;
    const width = layer.offsetWidth;
    const left = Math.min(
      Math.max(tip.x - width / 2, EDGE),
      window.innerWidth - width - EDGE,
    );
    const top = tip.place === "top" ? tip.y - layer.offsetHeight : tip.y;
    layer.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
  }, [tip]);

  if (!presence.mounted || !tip) return null;
  return createPortal(
    <div ref={layerRef} className="sg-hint-layer" aria-hidden="true">
      <div
        className="sg-tooltip sg-hint"
        data-place={tip.place}
        data-state={presence.state}
        onAnimationEnd={presence.onAnimationEnd}
      >
        <span>{tip.label}</span>
        {tip.keys.length > 0 && (
          <span className="sg-hint-keys">
            {tip.keys.map((key) => (
              <kbd key={key} className="sg-kbd">
                {key}
              </kbd>
            ))}
          </span>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default HintLayer;
